import React, { useState, useEffect, useCallback } from 'react';
import './GMPCompliance.css';
import { API_BASE } from '../utils/api';
import MRTTableAdapter from './MRTTableAdapter';
import { createMRTColumnHelper } from 'material-react-table';

interface GMPDashboard {
  total_sops: number;
  active_sops: number;
  equipment_count: number;
  equipment_due_maintenance: number;
  active_batches: number;
  completed_batches: number;
  pending_audits: number;
  open_findings: number;
  recent_activities: Array<{
    id: number;
    action_type: string;
    module: string;
    record_id: number;
    timestamp: string;
    user_id: number;
  }>;
}

interface SOP {
  id: number;
  title: string;
  description?: string;
  sop_type: string;
  version_number: string;
  effective_date: string;
  file_path?: string;
  status: string;
  facility_id?: number;
  company_id: number;
  created_by: number;
  approved_by?: number;
  approved_at?: string;
  created_at: string;
  updated_at: string;
}

interface Equipment {
  id: number;
  name: string;
  equipment_type: string;
  model_number?: string;
  serial_number?: string;
  location?: string;
  status: string;
  last_calibration?: string;
  next_calibration_due?: string;
  facility_id: number;
  company_id: number;
  created_at: string;
  updated_at: string;
}

interface Batch {
  id: number;
  batch_number: string;
  product_id: number;
  facility_id: number;
  company_id: number;
  size: string;
  start_date?: string;
  completion_date?: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface AuditPlan {
  id: number;
  title: string;
  audit_type: string;
  scope: string;
  planned_date: string;
  auditor?: string;
  facility_id: number;
  company_id: number;
  status: string;
  created_at: string;
  updated_at: string;
}

const GMPCompliance: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'sops' | 'equipment' | 'batches' | 'audits' | 'logs'>('dashboard');
  const [dashboardData, setDashboardData] = useState<GMPDashboard | null>(null);
  const [sops, setSops] = useState<SOP[]>([]);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [auditPlans, setAuditPlans] = useState<AuditPlan[]>([]);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createType, setCreateType] = useState<'sop' | 'equipment' | 'batch' | 'audit'>('sop');
  const [searchTerm, setSearchTerm] = useState('');
  const token = localStorage.getItem('token');
  const [facilities, setFacilities] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);

  // Equipment action modals
  const [showEquipmentView, setShowEquipmentView] = useState(false);
  const [showEquipmentMaintenance, setShowEquipmentMaintenance] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState<Equipment | null>(null);
  const [maintenanceForm, setMaintenanceForm] = useState<any>({ maintenance_type: 'calibration' });
  // SOP modals
  const [showSOPView, setShowSOPView] = useState(false);
  const [showSOPEdit, setShowSOPEdit] = useState(false);
  const [selectedSOP, setSelectedSOP] = useState<SOP | null>(null);
  const [sopEditForm, setSopEditForm] = useState<any>({});
  // Batch modals
  const [showBatchView, setShowBatchView] = useState(false);
  const [showBatchEdit, setShowBatchEdit] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<Batch | null>(null);
  const [batchEditForm, setBatchEditForm] = useState<any>({});
  // Audit findings modal
  const [showFindings, setShowFindings] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState<AuditPlan | null>(null);
  const [findings, setFindings] = useState<any[]>([]);
  const [findingForm, setFindingForm] = useState<any>({ status: 'open' });

  // Bind export with current state
  useEffect(() => {
    (window as any)._gmpExport = (kind: 'sops' | 'equipment' | 'batches' | 'audits') => {
      if (kind === 'sops') {
        const rows = sops.filter((s) => filterRow([s.title, s.sop_type, s.version_number, s.status], searchTerm));
        const headers = ['id','title','sop_type','version_number','effective_date','status','facility_id'];
        const csv = toCSV(rows.map((s) => ({
          id: s.id,
          title: s.title,
          sop_type: s.sop_type,
          version_number: s.version_number,
          effective_date: s.effective_date,
          status: s.status,
          facility_id: s.facility_id ?? ''
        })), headers);
        download('sops.csv', csv);
      } else if (kind === 'equipment') {
        const rows = equipment.filter((e) => filterRow([e.name, e.equipment_type, e.model_number || '', e.status], searchTerm));
        const headers = ['id','name','equipment_type','model_number','serial_number','location','status','last_calibration','next_calibration_due','facility_id'];
        const csv = toCSV(rows.map((e) => ({
          id: e.id,
          name: e.name,
          equipment_type: e.equipment_type,
          model_number: e.model_number || '',
          serial_number: e.serial_number || '',
          location: e.location || '',
          status: e.status,
          last_calibration: e.last_calibration || '',
          next_calibration_due: e.next_calibration_due || '',
          facility_id: e.facility_id
        })), headers);
        download('equipment.csv', csv);
      } else if (kind === 'batches') {
        const rows = batches.filter((b) => filterRow([b.batch_number, String(b.product_id), b.size, b.status], searchTerm));
        const headers = ['id','batch_number','product_id','facility_id','size','start_date','completion_date','status'];
        const csv = toCSV(rows.map((b) => ({
          id: b.id,
          batch_number: b.batch_number,
          product_id: b.product_id,
          facility_id: b.facility_id,
          size: b.size,
          start_date: b.start_date || '',
          completion_date: b.completion_date || '',
          status: b.status
        })), headers);
        download('batches.csv', csv);
      } else if (kind === 'audits') {
        const rows = auditPlans.filter((a) => filterRow([a.title, a.audit_type, a.scope, a.auditor || '', a.status], searchTerm));
        const headers = ['id','title','audit_type','scope','planned_date','auditor','facility_id','status'];
        const csv = toCSV(rows.map((a) => ({
          id: a.id,
          title: a.title,
          audit_type: a.audit_type,
          scope: a.scope,
          planned_date: a.planned_date,
          auditor: a.auditor || '',
          facility_id: a.facility_id,
          status: a.status
        })), headers);
        download('audits.csv', csv);
      }
    };
    return () => { try { delete (window as any)._gmpExport; } catch {} };
  }, [sops, equipment, batches, auditPlans, searchTerm]);

  useEffect(() => {
    // preload facilities and products for dropdowns
    (async () => {
      if (!token) return;
      try {
        const [fRes, pRes] = await Promise.all([
          fetch(`${API_BASE}/facilities/`, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(`${API_BASE}/products/`, { headers: { 'Authorization': `Bearer ${token}` } })
        ]);
        if (fRes.ok) setFacilities(await fRes.json());
        if (pRes.ok) setProducts(await pRes.json());
      } catch {}
    })();
  }, [token]);

  const fetchDashboardData = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_BASE}/gmp/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      }
    } catch (error) {
      console.error('Error fetching GMP dashboard data:', error);
    }
  }, [token]);

  const fetchSOPs = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_BASE}/sops/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setSops(data);
      }
    } catch (error) {
      console.error('Error fetching SOPs:', error);
    }
  }, [token]);

  const fetchEquipment = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_BASE}/equipment/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setEquipment(data);
      }
    } catch (error) {
      console.error('Error fetching equipment:', error);
    }
  }, [token]);

  const fetchBatches = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_BASE}/batches/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setBatches(data);
      }
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  }, [token]);

  const fetchAuditPlans = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_BASE}/audit-plans/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setAuditPlans(data);
      }
    } catch (error) {
      console.error('Error fetching audit plans:', error);
    }
  }, [token]);

  const fetchAuditLogs = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE}/audit-logs/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data);
      }
    } catch (e) {
      console.error('Error fetching audit logs', e);
    }
  }, [token]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    if (activeTab === 'sops') {
      fetchSOPs();
    } else if (activeTab === 'equipment') {
      fetchEquipment();
    } else if (activeTab === 'batches') {
      fetchBatches();
    } else if (activeTab === 'audits') {
      fetchAuditPlans();
    } else if (activeTab === 'logs') {
      fetchAuditLogs();
    }
  }, [activeTab, fetchSOPs, fetchEquipment, fetchBatches, fetchAuditPlans, fetchAuditLogs]);

  const handleCreate = async (formData: any) => {
    if (!token) return;
    
    setLoading(true);
    try {
      let endpoint = '';
      switch (createType) {
        case 'sop':
          endpoint = `${API_BASE}/sops/`;
          break;
        case 'equipment':
          endpoint = `${API_BASE}/equipment/`;
          break;
        case 'batch':
          endpoint = `${API_BASE}/batches/`;
          break;
        case 'audit':
          endpoint = `${API_BASE}/audit-plans/`;
          break;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setShowCreateModal(false);
        // Refresh the appropriate data
        if (createType === 'sop') {
          fetchSOPs();
        } else if (createType === 'equipment') {
          fetchEquipment();
        } else if (createType === 'batch') {
          fetchBatches();
        } else if (createType === 'audit') {
          fetchAuditPlans();
        }
        fetchDashboardData();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to create record'}`);
      }
    } catch (error) {
      console.error('Error creating record:', error);
      alert('Error creating record');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
      case 'completed':
      case 'in_use':
        return 'green';
      case 'pending':
      case 'planned':
      case 'in_progress':
        return 'orange';
      case 'obsolete':
      case 'retired':
      case 'cancelled':
        return 'red';
      default:
        return 'gray';
    }
  };

  const handleViewEquipment = (eq: Equipment) => {
    setSelectedEquipment(eq);
    setShowEquipmentView(true);
  };

  const handleMaintenance = (eq: Equipment) => {
    setSelectedEquipment(eq);
    setMaintenanceForm({ maintenance_type: 'calibration', description: '', performed_by: '', performed_at: '', next_maintenance_due: '', cost: '', notes: '' });
    setShowEquipmentMaintenance(true);
  };

  const submitMaintenance = async () => {
    if (!selectedEquipment || !token) return;
    setLoading(true);
    try {
      const body = {
        equipment_id: selectedEquipment.id,
        maintenance_type: maintenanceForm.maintenance_type,
        description: maintenanceForm.description,
        performed_by: maintenanceForm.performed_by || undefined,
        performed_at: maintenanceForm.performed_at || undefined,
        next_maintenance_due: maintenanceForm.next_maintenance_due || undefined,
        cost: maintenanceForm.cost ? Number(maintenanceForm.cost) : undefined,
        notes: maintenanceForm.notes || undefined,
      };
      const resp = await fetch(`${API_BASE}/equipment/${selectedEquipment.id}/maintenance`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        alert(`Failed to add maintenance: ${err.detail || resp.statusText}`);
      } else {
        setShowEquipmentMaintenance(false);
        await fetchEquipment();
        await fetchDashboardData();
      }
    } finally {
      setLoading(false);
    }
  };

  const renderEquipmentModals = () => {
    return (
      <>
        {showEquipmentView && selectedEquipment && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h3>Equipment Details</h3>
                <button className="btn btn-close" onClick={() => setShowEquipmentView(false)}>×</button>
              </div>
              <div className="modal-body">
                <div className="detail-grid">
                  <div><strong>Name:</strong> {selectedEquipment.name}</div>
                  <div><strong>Type:</strong> {selectedEquipment.equipment_type}</div>
                  <div><strong>Model:</strong> {selectedEquipment.model_number || '-'}</div>
                  <div><strong>Serial:</strong> {selectedEquipment.serial_number || '-'}</div>
                  <div><strong>Location:</strong> {selectedEquipment.location || '-'}</div>
                  <div><strong>Status:</strong> {selectedEquipment.status}</div>
                  <div><strong>Last Calibration:</strong> {selectedEquipment.last_calibration ? formatDate(selectedEquipment.last_calibration) : '-'}</div>
                  <div><strong>Next Due:</strong> {selectedEquipment.next_calibration_due ? formatDate(selectedEquipment.next_calibration_due) : '-'}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {showEquipmentMaintenance && selectedEquipment && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h3>Add Maintenance - {selectedEquipment.name}</h3>
                <button className="btn btn-close" onClick={() => setShowEquipmentMaintenance(false)}>×</button>
              </div>
              <div className="modal-body">
                <form onSubmit={(e) => { e.preventDefault(); submitMaintenance(); }}>
                  <div className="form-group">
                    <label>Maintenance Type *</label>
                    <select
                      required
                      value={maintenanceForm.maintenance_type}
                      onChange={(e) => setMaintenanceForm({ ...maintenanceForm, maintenance_type: e.target.value })}
                    >
                      <option value="calibration">Calibration</option>
                      <option value="repair">Repair</option>
                      <option value="inspection">Inspection</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Description *</label>
                    <textarea
                      required
                      value={maintenanceForm.description || ''}
                      onChange={(e) => setMaintenanceForm({ ...maintenanceForm, description: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Performed By</label>
                    <input
                      type="text"
                      value={maintenanceForm.performed_by || ''}
                      onChange={(e) => setMaintenanceForm({ ...maintenanceForm, performed_by: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Performed At</label>
                    <input
                      type="datetime-local"
                      value={maintenanceForm.performed_at || ''}
                      onChange={(e) => setMaintenanceForm({ ...maintenanceForm, performed_at: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Next Maintenance Due</label>
                    <input
                      type="date"
                      value={maintenanceForm.next_maintenance_due || ''}
                      onChange={(e) => setMaintenanceForm({ ...maintenanceForm, next_maintenance_due: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Cost</label>
                    <input
                      type="number"
                      step="0.01"
                      value={maintenanceForm.cost || ''}
                      onChange={(e) => setMaintenanceForm({ ...maintenanceForm, cost: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Notes</label>
                    <textarea
                      value={maintenanceForm.notes || ''}
                      onChange={(e) => setMaintenanceForm({ ...maintenanceForm, notes: e.target.value })}
                    />
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : 'Save Maintenance'}</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  // ---------- SOP handlers/modals ----------
  const handleViewSOP = (sop: SOP) => {
    setSelectedSOP(sop);
    setShowSOPView(true);
  };
  const handleEditSOP = (sop: SOP) => {
    setSelectedSOP(sop);
    setSopEditForm({
      title: sop.title,
      description: sop.description || '',
      sop_type: sop.sop_type,
      version_number: sop.version_number,
      effective_date: sop.effective_date?.slice(0,10),
      facility_id: sop.facility_id || ''
    });
    setShowSOPEdit(true);
  };
  const submitSopEdit = async () => {
    if (!selectedSOP || !token) return;
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE}/sops/${selectedSOP.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(sopEditForm)
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        alert(`Failed to update SOP: ${err.detail || resp.statusText}`);
      } else {
        setShowSOPEdit(false);
        await fetchSOPs();
        await fetchDashboardData();
      }
    } finally { setLoading(false); }
  };
  const renderSOPModals = () => (
    <>
      {showSOPView && selectedSOP && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>SOP Details</h3>
              <button className="btn btn-close" onClick={() => setShowSOPView(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div><strong>Title:</strong> {selectedSOP.title}</div>
                <div><strong>Type:</strong> {selectedSOP.sop_type}</div>
                <div><strong>Version:</strong> {selectedSOP.version_number}</div>
                <div><strong>Effective:</strong> {formatDate(selectedSOP.effective_date)}</div>
                <div><strong>Status:</strong> {selectedSOP.status}</div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showSOPEdit && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit SOP</h3>
              <button className="btn btn-close" onClick={() => setShowSOPEdit(false)}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={(e)=>{e.preventDefault(); submitSopEdit();}}>
                <div className="form-group">
                  <label>Title</label>
                  <input value={sopEditForm.title||''} onChange={(e)=>setSopEditForm({...sopEditForm,title:e.target.value})}/>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea value={sopEditForm.description||''} onChange={(e)=>setSopEditForm({...sopEditForm,description:e.target.value})}/>
                </div>
                <div className="form-group">
                  <label>SOP Type</label>
                  <input value={sopEditForm.sop_type||''} onChange={(e)=>setSopEditForm({...sopEditForm,sop_type:e.target.value})}/>
                </div>
                <div className="form-group">
                  <label>Version</label>
                  <input value={sopEditForm.version_number||''} onChange={(e)=>setSopEditForm({...sopEditForm,version_number:e.target.value})}/>
                </div>
                <div className="form-group">
                  <label>Effective Date</label>
                  <input type="date" value={sopEditForm.effective_date||''} onChange={(e)=>setSopEditForm({...sopEditForm,effective_date:e.target.value})}/>
                </div>
                <div className="form-group">
                  <label>Facility</label>
                  <select value={sopEditForm.facility_id||''} onChange={(e)=>setSopEditForm({...sopEditForm,facility_id:parseInt(e.target.value)})}>
                    <option value="">Select Facility</option>
                    {facilities.map((f:any)=>(<option key={f.id} value={f.id}>{f.name} (ID {f.id})</option>))}
                  </select>
                </div>
                <div className="form-actions">
                  <button className="btn btn-primary" disabled={loading}>{loading?'Saving...':'Save'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // ---------- Batch handlers/modals ----------
  const handleViewBatch = (b: Batch) => { setSelectedBatch(b); setShowBatchView(true); };
  const handleEditBatch = (b: Batch) => {
    setSelectedBatch(b);
    setBatchEditForm({
      batch_number: b.batch_number,
      size: b.size,
      start_date: b.start_date?.slice(0,10) || '',
      completion_date: b.completion_date?.slice(0,10) || '',
      status: b.status
    });
    setShowBatchEdit(true);
  };
  const submitBatchEdit = async () => {
    if (!selectedBatch || !token) return;
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE}/batches/${selectedBatch.id}`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(batchEditForm)
      });
      if (!resp.ok) { const err=await resp.json().catch(()=>({})); alert(`Failed to update batch: ${err.detail||resp.statusText}`);} else {
        setShowBatchEdit(false); await fetchBatches(); await fetchDashboardData();
      }
    } finally { setLoading(false); }
  };
  const renderBatchModals = () => (
    <>
      {showBatchView && selectedBatch && (
        <div className="modal-overlay"><div className="modal-content"><div className="modal-header"><h3>Batch Details</h3><button className="btn btn-close" onClick={()=>setShowBatchView(false)}>×</button></div><div className="modal-body"><div className="detail-grid"><div><strong>Batch #:</strong> {selectedBatch.batch_number}</div><div><strong>Product ID:</strong> {selectedBatch.product_id}</div><div><strong>Size:</strong> {selectedBatch.size}</div><div><strong>Status:</strong> {selectedBatch.status}</div></div></div></div></div>
      )}
      {showBatchEdit && (
        <div className="modal-overlay"><div className="modal-content"><div className="modal-header"><h3>Edit Batch</h3><button className="btn btn-close" onClick={()=>setShowBatchEdit(false)}>×</button></div><div className="modal-body"><form onSubmit={(e)=>{e.preventDefault(); submitBatchEdit();}}><div className="form-group"><label>Batch Number</label><input value={batchEditForm.batch_number||''} onChange={(e)=>setBatchEditForm({...batchEditForm,batch_number:e.target.value})}/></div><div className="form-group"><label>Size</label><input value={batchEditForm.size||''} onChange={(e)=>setBatchEditForm({...batchEditForm,size:e.target.value})}/></div><div className="form-group"><label>Start Date</label><input type="date" value={batchEditForm.start_date||''} onChange={(e)=>setBatchEditForm({...batchEditForm,start_date:e.target.value})}/></div><div className="form-group"><label>Completion Date</label><input type="date" value={batchEditForm.completion_date||''} onChange={(e)=>setBatchEditForm({...batchEditForm,completion_date:e.target.value})}/></div><div className="form-group"><label>Status</label><input value={batchEditForm.status||''} onChange={(e)=>setBatchEditForm({...batchEditForm,status:e.target.value})}/></div><div className="form-actions"><button className="btn btn-primary" disabled={loading}>{loading?'Saving...':'Save'}</button></div></form></div></div></div>
      )}
    </>
  );

  // ---------- Audit findings ----------
  const openFindings = async (audit: AuditPlan) => {
    setSelectedAudit(audit); setShowFindings(true);
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/audit-plans/${audit.id}/findings`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setFindings(await res.json()); else setFindings([]);
    } catch { setFindings([]); }
  };
  const submitFinding = async () => {
    if (!selectedAudit || !token) return;
    const body = { ...findingForm };
    const res = await fetch(`${API_BASE}/audit-plans/${selectedAudit.id}/findings`, { method:'POST', headers:{'Authorization':`Bearer ${token}`,'Content-Type':'application/json'}, body: JSON.stringify(body)});
    if (res.ok) { await openFindings(selectedAudit); setFindingForm({ status:'open' }); }
    else { const err=await res.json().catch(()=>({})); alert(`Failed to add finding: ${err.detail||res.statusText}`); }
  };
  const renderFindingsModal = () => (
    <>
      {showFindings && selectedAudit && (
        <div className="modal-overlay"><div className="modal-content"><div className="modal-header"><h3>Audit Findings - {selectedAudit.title}</h3><button className="btn btn-close" onClick={()=>setShowFindings(false)}>×</button></div><div className="modal-body"><div className="table-container"><table className="data-table"><thead><tr><th>Title</th><th>Severity</th><th>Status</th></tr></thead><tbody>{findings.map((f:any)=>(<tr key={f.id}><td>{f.title||f.description||'-'}</td><td>{f.severity||'-'}</td><td>{f.status||'-'}</td></tr>))}</tbody></table></div><h4>Add Finding</h4><div className="form-group"><label>Title</label><input value={findingForm.title||''} onChange={(e)=>setFindingForm({...findingForm,title:e.target.value})}/></div><div className="form-group"><label>Severity</label><input value={findingForm.severity||''} onChange={(e)=>setFindingForm({...findingForm,severity:e.target.value})}/></div><div className="form-group"><label>Status</label><select value={findingForm.status||'open'} onChange={(e)=>setFindingForm({...findingForm,status:e.target.value})}><option value="open">Open</option><option value="closed">Closed</option></select></div><div className="form-actions"><button className="btn btn-primary" onClick={submitFinding}>Add Finding</button></div></div></div></div>
      )}
    </>
  );

  const renderDashboard = () => {
    if (!dashboardData) return <div>Loading dashboard...</div>;

    return (
      <div className="gmp-dashboard">
        <h2>GMP Compliance Dashboard</h2>
        
        <div className="dashboard-stats">
          <div className="stat-card">
            <h3>SOPs</h3>
            <div className="stat-number">{dashboardData.total_sops}</div>
            <div className="stat-detail">Active: {dashboardData.active_sops}</div>
          </div>
          
          <div className="stat-card">
            <h3>Equipment</h3>
            <div className="stat-number">{dashboardData.equipment_count}</div>
            <div className="stat-detail">Due Maintenance: {dashboardData.equipment_due_maintenance}</div>
          </div>
          
          <div className="stat-card">
            <h3>Batches</h3>
            <div className="stat-number">{dashboardData.active_batches}</div>
            <div className="stat-detail">Completed: {dashboardData.completed_batches}</div>
          </div>
          
          <div className="stat-card">
            <h3>Audits</h3>
            <div className="stat-number">{dashboardData.pending_audits}</div>
            <div className="stat-detail">Open Findings: {dashboardData.open_findings}</div>
          </div>
        </div>

        <div className="recent-activities">
          <h3>Recent Activities</h3>
          <div className="activity-list">
            {dashboardData.recent_activities.map((activity) => (
              <div key={activity.id} className="activity-item">
                <span className="activity-action">{activity.action_type}</span>
                <span className="activity-module">{activity.module}</span>
                <span className="activity-time">{formatDate(activity.timestamp)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderSOPs = () => {
    const columnHelper = createMRTColumnHelper<SOP>();
    const columns: any[] = [
      columnHelper.accessor('title', { 
        header: 'Title', 
        size: 200,
        filterVariant: 'text'
      }),
      columnHelper.accessor('sop_type', { 
        header: 'Type', 
        size: 140,
        filterVariant: 'select'
      }),
      columnHelper.accessor('version_number', { 
        header: 'Version', 
        size: 100,
        filterVariant: 'text'
      }),
      columnHelper.accessor(row => (row.effective_date ? formatDate(row.effective_date) : '-'), { 
        id: 'effective_date', 
        header: 'Effective Date', 
        size: 130,
        filterVariant: 'date-range'
      }),
      columnHelper.accessor('status', { 
        header: 'Status', 
        size: 120,
        filterVariant: 'select',
        Cell: ({ cell }) => (
          <span className={`status-badge status-${getStatusColor(cell.getValue())}`}>
            {cell.getValue()}
          </span>
        )
      }),
      columnHelper.accessor(row => formatDate(row.created_at), { 
        id: 'created_at', 
        header: 'Created', 
        size: 130,
        filterVariant: 'date-range'
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        size: 160,
        Cell: ({ row }) => (
          <div className="flex justify-end space-x-2">
            <button className="btn btn-sm" onClick={(e) => { e.stopPropagation(); handleViewSOP(row.original); }}>View</button>
            <button className="btn btn-sm" onClick={(e) => { e.stopPropagation(); handleEditSOP(row.original); }}>Edit</button>
          </div>
        ),
      }),
    ];

    return (
      <div className="gmp-section">
        <div className="section-header">
          <h2>Standard Operating Procedures</h2>
          <button 
            className="btn btn-primary"
            onClick={() => {
              setCreateType('sop');
              setShowCreateModal(true);
            }}
          >
            + New SOP
          </button>
          <div className="section-actions">
            <button className="btn" onClick={() => exportCSV('sops')}>Export CSV</button>
          </div>
        </div>
        
        <div className="table-container">
          <MRTTableAdapter<SOP> columns={columns} data={sops} />
        </div>
      </div>
    );
  };

  const renderEquipment = () => {
    const columnHelper = createMRTColumnHelper<Equipment>();
    const columns: any[] = [
      columnHelper.accessor('name', { 
        header: 'Name', 
        size: 180,
        filterVariant: 'text'
      }),
      columnHelper.accessor('equipment_type', { 
        header: 'Type', 
        size: 140,
        filterVariant: 'select'
      }),
      columnHelper.accessor('model_number', { 
        header: 'Model', 
        size: 120,
        filterVariant: 'text',
        Cell: ({ cell }) => cell.getValue() || '-'
      }),
      columnHelper.accessor('location', { 
        header: 'Location', 
        size: 140,
        filterVariant: 'text',
        Cell: ({ cell }) => cell.getValue() || '-'
      }),
      columnHelper.accessor('status', { 
        header: 'Status', 
        size: 120,
        filterVariant: 'select',
        Cell: ({ cell }) => (
          <span className={`status-badge status-${getStatusColor(cell.getValue())}`}>
            {cell.getValue()}
          </span>
        )
      }),
      columnHelper.accessor(row => (row.last_calibration ? formatDate(row.last_calibration) : '-'), { 
        id: 'last_calibration', 
        header: 'Last Calibration', 
        size: 140,
        filterVariant: 'date-range'
      }),
      columnHelper.accessor(row => (row.next_calibration_due ? formatDate(row.next_calibration_due) : '-'), { 
        id: 'next_calibration_due', 
        header: 'Next Due', 
        size: 130,
        filterVariant: 'date-range'
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        size: 180,
        Cell: ({ row }) => (
          <div className="flex justify-end space-x-2">
            <button className="btn btn-sm" onClick={(e) => { e.stopPropagation(); handleViewEquipment(row.original); }}>View</button>
            <button className="btn btn-sm" onClick={(e) => { e.stopPropagation(); handleMaintenance(row.original); }}>Maintenance</button>
          </div>
        ),
      }),
    ];

    return (
      <div className="gmp-section">
        <div className="section-header">
          <h2>Equipment Management</h2>
          <button 
            className="btn btn-primary"
            onClick={() => {
              setCreateType('equipment');
              setShowCreateModal(true);
            }}
          >
            + New Equipment
          </button>
          <div className="section-actions">
            <button className="btn" onClick={() => exportCSV('equipment')}>Export CSV</button>
          </div>
        </div>
        
        <div className="table-container">
          <MRTTableAdapter<Equipment> columns={columns} data={equipment} />
        </div>
      </div>
    );
  };

  const renderBatches = () => {
    const columnHelper = createMRTColumnHelper<Batch>();
    const columns: any[] = [
      columnHelper.accessor('batch_number', { 
        header: 'Batch Number', 
        size: 140,
        filterVariant: 'text'
      }),
      columnHelper.accessor('product_id', { 
        header: 'Product ID', 
        size: 120,
        filterVariant: 'text'
      }),
      columnHelper.accessor('size', { 
        header: 'Size', 
        size: 100,
        filterVariant: 'text'
      }),
      columnHelper.accessor(row => (row.start_date ? formatDate(row.start_date) : '-'), { 
        id: 'start_date', 
        header: 'Start Date', 
        size: 130,
        filterVariant: 'date-range'
      }),
      columnHelper.accessor(row => (row.completion_date ? formatDate(row.completion_date) : '-'), { 
        id: 'completion_date', 
        header: 'Completion Date', 
        size: 140,
        filterVariant: 'date-range'
      }),
      columnHelper.accessor('status', { 
        header: 'Status', 
        size: 120,
        filterVariant: 'select',
        Cell: ({ cell }) => (
          <span className={`status-badge status-${getStatusColor(cell.getValue())}`}>
            {cell.getValue()}
          </span>
        )
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        size: 160,
        Cell: ({ row }) => (
          <div className="flex justify-end space-x-2">
            <button className="btn btn-sm" onClick={(e) => { e.stopPropagation(); handleViewBatch(row.original); }}>View</button>
            <button className="btn btn-sm" onClick={(e) => { e.stopPropagation(); handleEditBatch(row.original); }}>Edit</button>
          </div>
        ),
      }),
    ];

    return (
      <div className="gmp-section">
        <div className="section-header">
          <h2>Batch Manufacturing Records</h2>
          <button 
            className="btn btn-primary"
            onClick={() => {
              setCreateType('batch');
              setShowCreateModal(true);
            }}
          >
            + New Batch
          </button>
          <div className="section-actions">
            <button className="btn" onClick={() => exportCSV('batches')}>Export CSV</button>
          </div>
        </div>
        
        <div className="table-container">
          <MRTTableAdapter<Batch> columns={columns} data={batches} />
        </div>
      </div>
    );
  };

  const renderAudits = () => {
    const columnHelper = createMRTColumnHelper<AuditPlan>();
    const columns: any[] = [
      columnHelper.accessor('title', { header: 'Title', size: 240 }),
      columnHelper.accessor('audit_type', { header: 'Type', size: 140, filterVariant: 'select' }),
      columnHelper.accessor('scope', { header: 'Scope', size: 260 }),
      columnHelper.accessor(row => (row.planned_date ? new Date(row.planned_date).toISOString().slice(0,10) : ''), { id: 'planned_date', header: 'Planned Date', size: 130, filterVariant: 'date-range' }),
      columnHelper.accessor(row => (row.auditor || '-'), { id: 'auditor', header: 'Auditor', size: 140 }),
      columnHelper.accessor('status', { header: 'Status', size: 120, filterVariant: 'select' }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        size: 160,
        Cell: ({ row }) => (
          <div className="flex justify-end space-x-2">
            <button className="btn btn-sm" onClick={(e) => { e.stopPropagation(); /* simple view */ openFindings(row.original); }}>View</button>
            <button className="btn btn-sm" onClick={(e) => { e.stopPropagation(); openFindings(row.original); }}>Findings</button>
          </div>
        ),
      }),
    ];
    const filtered = auditPlans.filter((a) => filterRow([a.title, a.audit_type, a.scope, a.auditor || '', a.status], searchTerm));
    return (
      <div className="gmp-section">
        <div className="section-header">
          <h2>Audit Management</h2>
          <button 
            className="btn btn-primary"
            onClick={() => { setCreateType('audit'); setShowCreateModal(true); }}
          >
            + New Audit Plan
          </button>
          <div className="section-actions">
            <input className="input" placeholder="Search..." value={searchTerm} onChange={(e)=>setSearchTerm(e.target.value)} />
            <button className="btn" onClick={() => exportCSV('audits')}>Export CSV</button>
          </div>
        </div>
        <div className="table-container">
          <MRTTableAdapter<AuditPlan> columns={columns} data={filtered} />
        </div>
      </div>
    );
  };

  const renderAuditLogs = () => {
    const columnHelper = createMRTColumnHelper<any>();
    const columns: any[] = [
      columnHelper.accessor(row => formatDate(row.timestamp), { 
        id: 'timestamp', 
        header: 'Time', 
        size: 160,
        filterVariant: 'date-range'
      }),
      columnHelper.accessor(row => (row.user_name || row.user_id), { 
        id: 'user', 
        header: 'User', 
        size: 140,
        filterVariant: 'text'
      }),
      columnHelper.accessor('module', { 
        header: 'Module', 
        size: 120,
        filterVariant: 'select'
      }),
      columnHelper.accessor('action_type', { 
        header: 'Action', 
        size: 140,
        filterVariant: 'select'
      }),
      columnHelper.accessor('record_id', { 
        header: 'Record ID', 
        size: 100,
        filterVariant: 'text'
      }),
    ];

    return (
      <div className="gmp-section">
        <div className="section-header">
          <h2>Audit Logs</h2>
          <div className="section-actions">
            <button className="btn" onClick={() => exportCSV('audits')}>Export CSV</button>
          </div>
        </div>
        <div className="table-container">
          <MRTTableAdapter<any> columns={columns} data={auditLogs} />
        </div>
      </div>
    );
  };

  const renderCreateModal = () => {
    if (!showCreateModal) return null;

    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
            <h3>Create New {createType.toUpperCase()}</h3>
            <button 
              className="btn btn-close"
              onClick={() => setShowCreateModal(false)}
            >
              ×
            </button>
          </div>
          
          <div className="modal-body">
            <CreateForm 
              type={createType} 
              onSubmit={handleCreate}
              loading={loading}
              facilities={facilities}
              products={products}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="gmp-compliance">
      <div className="gmp-header">
        <h1>GMP Compliance & Audits</h1>
        <p>Manage Good Manufacturing Practices, SOPs, equipment, batches, and audits</p>
      </div>

      <div className="gmp-tabs">
        <button 
          className={`tab ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          Dashboard
        </button>
        <button 
          className={`tab ${activeTab === 'sops' ? 'active' : ''}`}
          onClick={() => setActiveTab('sops')}
        >
          SOPs
        </button>
        <button 
          className={`tab ${activeTab === 'equipment' ? 'active' : ''}`}
          onClick={() => setActiveTab('equipment')}
        >
          Equipment
        </button>
        <button 
          className={`tab ${activeTab === 'batches' ? 'active' : ''}`}
          onClick={() => setActiveTab('batches')}
        >
          Batches
        </button>
        <button 
          className={`tab ${activeTab === 'audits' ? 'active' : ''}`}
          onClick={() => setActiveTab('audits')}
        >
          Audits
        </button>
        <button 
          className={`tab ${activeTab === 'logs' ? 'active' : ''}`}
          onClick={() => setActiveTab('logs')}
        >
          Audit Logs
        </button>
      </div>

      <div className="gmp-content">
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'sops' && renderSOPs()}
        {activeTab === 'equipment' && renderEquipment()}
        {activeTab === 'batches' && renderBatches()}
        {activeTab === 'audits' && renderAudits()}
        {activeTab === 'logs' && renderAuditLogs()}
      </div>

      {renderCreateModal()}
      {renderEquipmentModals()}
      {renderSOPModals()}
      {renderBatchModals()}
      {renderFindingsModal()}
    </div>
  );
};

// Create Form Component
interface CreateFormProps {
  type: 'sop' | 'equipment' | 'batch' | 'audit';
  onSubmit: (data: any) => void;
  loading: boolean;
  facilities: any[];
  products: any[];
}

const CreateForm: React.FC<CreateFormProps> = ({ type, onSubmit, loading, facilities, products }) => {
  const [formData, setFormData] = useState<any>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleChange = (field: string, value: any) => {
    setFormData({ ...formData, [field]: value });
  };

  const renderSOPForm = () => (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label>Title *</label>
        <input
          type="text"
          required
          value={formData.title || ''}
          onChange={(e) => handleChange('title', e.target.value)}
        />
      </div>
      <div className="form-group">
        <label>Description</label>
        <textarea
          value={formData.description || ''}
          onChange={(e) => handleChange('description', e.target.value)}
        />
      </div>
      <div className="form-group">
        <label>SOP Type *</label>
        <select
          required
          value={formData.sop_type || ''}
          onChange={(e) => handleChange('sop_type', e.target.value)}
        >
          <option value="">Select Type</option>
          <option value="cleaning">Cleaning</option>
          <option value="mixing">Mixing</option>
          <option value="equipment_use">Equipment Use</option>
          <option value="labeling">Labeling</option>
          <option value="safety">Safety</option>
        </select>
      </div>
      <div className="form-group">
        <label>Version Number *</label>
        <input
          type="text"
          required
          value={formData.version_number || '1.0'}
          onChange={(e) => handleChange('version_number', e.target.value)}
        />
      </div>
      <div className="form-group">
        <label>Effective Date *</label>
        <input
          type="date"
          required
          value={formData.effective_date || ''}
          onChange={(e) => handleChange('effective_date', e.target.value)}
        />
      </div>
      <div className="form-group">
        <label>Facility *</label>
        <select
          required
          value={formData.facility_id || ''}
          onChange={(e) => handleChange('facility_id', parseInt(e.target.value))}
        >
          <option value="">Select Facility</option>
          {facilities.map((f:any) => (
            <option key={f.id} value={f.id}>{f.name} (ID {f.id})</option>
          ))}
        </select>
      </div>
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Creating...' : 'Create SOP'}
        </button>
      </div>
    </form>
  );

  const renderEquipmentForm = () => (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label>Name *</label>
        <input
          type="text"
          required
          value={formData.name || ''}
          onChange={(e) => handleChange('name', e.target.value)}
        />
      </div>
      <div className="form-group">
        <label>Equipment Type *</label>
        <select
          required
          value={formData.equipment_type || ''}
          onChange={(e) => handleChange('equipment_type', e.target.value)}
        >
          <option value="">Select Type</option>
          <option value="mixer">Mixer</option>
          <option value="filler">Filler</option>
          <option value="tester">Tester</option>
          <option value="packaging">Packaging</option>
        </select>
      </div>
      <div className="form-group">
        <label>Model Number</label>
        <input
          type="text"
          value={formData.model_number || ''}
          onChange={(e) => handleChange('model_number', e.target.value)}
        />
      </div>
      <div className="form-group">
        <label>Serial Number</label>
        <input
          type="text"
          value={formData.serial_number || ''}
          onChange={(e) => handleChange('serial_number', e.target.value)}
        />
      </div>
      <div className="form-group">
        <label>Location</label>
        <input
          type="text"
          value={formData.location || ''}
          onChange={(e) => handleChange('location', e.target.value)}
        />
      </div>
      <div className="form-group">
        <label>Facility *</label>
        <select
          required
          value={formData.facility_id || ''}
          onChange={(e) => handleChange('facility_id', parseInt(e.target.value))}
        >
          <option value="">Select Facility</option>
          {facilities.map((f:any) => (
            <option key={f.id} value={f.id}>{f.name} (ID {f.id})</option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label>Last Calibration</label>
        <input
          type="date"
          value={formData.last_calibration || ''}
          onChange={(e) => handleChange('last_calibration', e.target.value)}
        />
      </div>
      <div className="form-group">
        <label>Next Calibration Due</label>
        <input
          type="date"
          value={formData.next_calibration_due || ''}
          onChange={(e) => handleChange('next_calibration_due', e.target.value)}
        />
      </div>
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Creating...' : 'Create Equipment'}
        </button>
      </div>
    </form>
  );

  const renderBatchForm = () => (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label>Batch Number *</label>
        <input
          type="text"
          required
          value={formData.batch_number || ''}
          onChange={(e) => handleChange('batch_number', e.target.value)}
        />
      </div>
      <div className="form-group">
        <label>Product *</label>
        <select
          required
          value={formData.product_id || ''}
          onChange={(e) => handleChange('product_id', parseInt(e.target.value))}
        >
          <option value="">Select Product</option>
          {products.map((p:any) => (
            <option key={p.id} value={p.id}>{p.name} (ID {p.id})</option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label>Facility *</label>
        <select
          required
          value={formData.facility_id || ''}
          onChange={(e) => handleChange('facility_id', parseInt(e.target.value))}
        >
          <option value="">Select Facility</option>
          {facilities.map((f:any) => (
            <option key={f.id} value={f.id}>{f.name} (ID {f.id})</option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label>Size *</label>
        <input
          type="text"
          required
          placeholder="e.g., 100 kg, 200 liters"
          value={formData.size || ''}
          onChange={(e) => handleChange('size', e.target.value)}
        />
      </div>
      <div className="form-group">
        <label>Start Date</label>
        <input
          type="date"
          value={formData.start_date || ''}
          onChange={(e) => handleChange('start_date', e.target.value)}
        />
      </div>
      <div className="form-group">
        <label>Completion Date</label>
        <input
          type="date"
          value={formData.completion_date || ''}
          onChange={(e) => handleChange('completion_date', e.target.value)}
        />
      </div>
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Creating...' : 'Create Batch'}
        </button>
      </div>
    </form>
  );

  const renderAuditForm = () => (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label>Title *</label>
        <input
          type="text"
          required
          value={formData.title || ''}
          onChange={(e) => handleChange('title', e.target.value)}
        />
      </div>
      <div className="form-group">
        <label>Audit Type *</label>
        <select
          required
          value={formData.audit_type || ''}
          onChange={(e) => handleChange('audit_type', e.target.value)}
        >
          <option value="">Select Type</option>
          <option value="internal">Internal</option>
          <option value="external">External</option>
          <option value="regulatory">Regulatory</option>
        </select>
      </div>
      <div className="form-group">
        <label>Scope *</label>
        <textarea
          required
          value={formData.scope || ''}
          onChange={(e) => handleChange('scope', e.target.value)}
        />
      </div>
      <div className="form-group">
        <label>Planned Date *</label>
        <input
          type="date"
          required
          value={formData.planned_date || ''}
          onChange={(e) => handleChange('planned_date', e.target.value)}
        />
      </div>
      <div className="form-group">
        <label>Auditor</label>
        <input
          type="text"
          value={formData.auditor || ''}
          onChange={(e) => handleChange('auditor', e.target.value)}
        />
      </div>
      <div className="form-group">
        <label>Facility *</label>
        <select
          required
          value={formData.facility_id || ''}
          onChange={(e) => handleChange('facility_id', parseInt(e.target.value))}
        >
          <option value="">Select Facility</option>
          {facilities.map((f:any) => (
            <option key={f.id} value={f.id}>{f.name} (ID {f.id})</option>
          ))}
        </select>
      </div>
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Creating...' : 'Create Audit Plan'}
        </button>
      </div>
    </form>
  );

  switch (type) {
    case 'sop':
      return renderSOPForm();
    case 'equipment':
      return renderEquipmentForm();
    case 'batch':
      return renderBatchForm();
    case 'audit':
      return renderAuditForm();
    default:
      return null;
  }
};

export default GMPCompliance;

// ---------- Helpers ----------
function filterRow(values: Array<string>, term: string): boolean {
  if (!term) return true;
  const t = term.toLowerCase();
  return values.some((v) => (v || '').toLowerCase().includes(t));
}

function toCSV(rows: any[], headers: string[]): string {
  const esc = (v: any) => {
    const s = v == null ? '' : String(v);
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  };
  const lines = [headers.join(',')];
  for (const r of rows) {
    lines.push(headers.map((h) => esc((r as any)[h])).join(','));
  }
  return lines.join('\n');
}

function download(filename: string, content: string, mime = 'text/csv;charset=utf-8;') {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function exportCSV(kind: 'sops' | 'equipment' | 'batches' | 'audits') {
  // This is a shim; at runtime, we will bind a closure capturing state via window._gmpExport
  if ((window as any)._gmpExport) {
    (window as any)._gmpExport(kind);
  }
}

